import { CommonModule } from "@angular/common";
import { Component, Input, OnChanges, SimpleChanges } from "@angular/core";
import { TranslateModule } from "@ngx-translate/core";
import { HouseType } from "@shared/enums/houseType";
import { PackagesType } from "@shared/enums/packages-type";
import { RiskItemInformations } from "@shared/enums/riskItemInformations";
import { PolicyDetail } from "@shared/models/policy-details";
import { RiskItem, RiskItemProductCondition, RiskItemRiskCodeCondition } from "@shared/models/risk-item.model";
import { BooleanToTranslationPipe } from "@shared/pipes/boolean-to-translation.pipe";
import { StringToBooleanPipe } from "@shared/pipes/string-to-boolean.pipe";
import { DividerModule } from "primeng/divider";

@Component({
  selector: "app-housing-options-mrh",
  standalone: true,
  imports: [CommonModule, TranslateModule, DividerModule, StringToBooleanPipe, BooleanToTranslationPipe],
  templateUrl: "./housing-options-mrh.component.html",
  styleUrl: "./housing-options-mrh.component.scss",
})
export class HousingOptionsMrhComponent implements OnChanges {
  @Input() details!: PolicyDetail;

  riskItemInfo = RiskItemInformations;
  package = PackagesType;

  riskItems: { value: string; label: string }[] = [];

  riskItemConfig: RiskItem[] = [
    {
      code: this.riskItemInfo.NATURE_LOG,
      label: "housingSection.typeResidence",
    },
    {
      code: this.riskItemInfo.NATURE_OCCU,
      label: "housingSection.qualityOccupant",
    },
    {
      code: this.riskItemInfo.HAB_TYPE,
      label: "housingSection.houseType",
    },
    {
      code: this.riskItemInfo.NBR_PIECES,
      label: "housingSection.nbrPieces",
    },
    {
      code: this.riskItemInfo.NBR_PIECES_40M2,
      label: "housingSection.nbrPieces40",
      condition: {
        product: [this.package.ConfortPlus, this.package.Confort],
      },
    },
    {
      code: this.riskItemInfo.ADR_ETAGE,
      label: "housingSection.floorHouse",
      condition: {
        code: this.riskItemInfo.HAB_TYPE,
        value: HouseType.Appartement,
        result: undefined as boolean | undefined,
      },
    },
    {
      code: this.riskItemInfo.SURFACE_DEP,
      label: "housingSection.dependance",
      condition: {
        product: [this.package.ConfortPlus, this.package.Confort],
      },
    },
    {
      code: this.riskItemInfo.POSSEDE_PISCINE,
      label: "housingSection.pool",
      condition: {
        product: [this.package.ConfortPlus, this.package.Confort],
      },
    },
    {
      code: this.riskItemInfo.POSSEDE_VERANDA,
      label: "housingSection.veranda",
      condition: {
        product: [this.package.ConfortPlus, this.package.Confort],
      },
    },
    {
      code: this.riskItemInfo.POSSEDE_ENERGIEV,
      label: "housingSection.renewableEnergy",
      condition: {
        product: [this.package.ConfortPlus, this.package.Confort],
      },
    },
  ];

  ngOnChanges(change: SimpleChanges): void {
    if (change["details"]) {
      this.riskItems = this.getOrderedRiskItems();
    }
  }

  getOrderedRiskItems() {
    if (!this.details || !this.details.riskItemInformations || !this.details.riskItemInformations.values) {
      return [];
    }

    const product = this.details.formulasAndOptionsInformations.formulaCode as PackagesType;

    return this.riskItemConfig
      .map(config => {
        const item = this.details.riskItemInformations.values.find(i => i.code === config.code);
        if (item) {
          //Check if condition and apply it
          if (config.condition) {
            const productCondition = config.condition as RiskItemProductCondition;
            const codeCondition = config.condition as RiskItemRiskCodeCondition;
            //Check if the condition is a product or a code
            if (productCondition.product) {
              config.condition.result = productCondition.product.includes(product);
            } else if (codeCondition.code) {
              const conditionValue = this.details.riskItemInformations.values.find(i => i.code === codeCondition.code);
              config.condition.result = conditionValue?.value === codeCondition.value;
            }
          }
          return {
            value: item.value,
            label: config.label,
            show: config.condition?.result ?? true, //If no condition, show it, else show it if the condition is true
          };
        }
        return null;
      })
      .filter(item => item !== null && item.show) as {
      value: string;
      label: string;
    }[]; //Filter out the items that are null and the ones that should not be shown
  }
}
